import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

interface BannerItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  linkUrl?: string;
  isActive: boolean;
  order: number;
}

// Dosya tipini URL'den tespit et
const getMediaType = (url: string): 'video' | 'gif' | 'image' => {
  const extension = url.split('.').pop()?.toLowerCase().split('?')[0];
  if (extension === 'mp4' || extension === 'webm' || extension === 'ogg') {
    return 'video';
  }
  if (extension === 'gif') {
    return 'gif';
  }
  return 'image';
};

// Medya içeriğini render eden bileşen
const MediaContent: React.FC<{ url: string; title: string }> = ({ url, title }) => {
  const mediaType = getMediaType(url);

  if (mediaType === 'video') {
    return (
      <video
        src={url}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  if (mediaType === 'gif') {
    return (
      <img
        src={url}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  return (
    <div
      className="absolute inset-0 w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${url})` }}
    />
  );
};

const Banner: React.FC = () => {
  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    const bannersQuery = query(
      collection(db, 'banners'),
      where('isActive', '==', true),
      orderBy('order', 'asc')
    );

    const unsubscribe = onSnapshot(bannersQuery, (snapshot) => {
      const bannersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BannerItem[];
      setBanners(bannersData);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, banners.length]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  if (banners.length === 0) {
    return (
      <div className="relative w-full h-[200px] md:h-[220px] lg:h-[320px] bg-gradient-to-r from-dark-800 to-dark-700 rounded-2xl overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-500/20 flex items-center justify-center">
            <ChevronRight className="w-8 h-8 text-primary-400" />
          </div>
          <p className="text-gray-400">Banner yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-[200px] md:h-[220px] lg:h-[320px] rounded-2xl overflow-hidden group"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          {banners[currentIndex]?.linkUrl ? (
            <a
              href={banners[currentIndex].linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full h-full"
            >
              <div className="w-full h-full relative">
                <MediaContent
                  url={banners[currentIndex].imageUrl}
                  title={banners[currentIndex].title}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-dark-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-2xl md:text-4xl font-bold text-white mb-2"
                  >
                    {banners[currentIndex].title}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-gray-300 text-sm md:text-lg max-w-2xl"
                  >
                    {banners[currentIndex].description}
                  </motion.p>
                </div>
              </div>
            </a>
          ) : (
            <div className="w-full h-full relative">
              <MediaContent
                url={banners[currentIndex]?.imageUrl}
                title={banners[currentIndex]?.title}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-dark-900/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl md:text-4xl font-bold text-white mb-2"
                >
                  {banners[currentIndex]?.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-300 text-sm md:text-lg max-w-2xl"
                >
                  {banners[currentIndex]?.description}
                </motion.p>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      {banners.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-dark-800/80 backdrop-blur-sm border border-dark-600 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-dark-700"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-dark-800/80 backdrop-blur-sm border border-dark-600 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-dark-700"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </>
      )}

      {/* Dots Navigation */}
      {banners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-primary-500 w-6 md:w-8'
                  : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Banner;
